import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import SocialAuthButtons from "../components/SocialAuthButtons";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [twoFactor, setTwoFactor] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Arriving from the Pro plan: a new shop signs up as a business and ends on the Pro payment page.
  const registerTo =
    params.get("next") === "/business/subscription" ? "/register?role=business_owner&type=business&plan=pro" : "/register";

  const goHome = (user) => {
    const roleHome = { admin: "/admin", business_owner: "/business" }[user.role] || "/account";
    navigate(params.get("next") || roleHome);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(form);
      if (result?.requires2fa) {
        setTwoFactor({ phone: result.phone, email: result.email, sentTo: result.sentTo });
      } else {
        goHome(result);
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  const onVerify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const user = await verify2fa({ ...twoFactor, code });
      goHome(user);
    } catch (e) {
      setError(e?.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  if (twoFactor) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
        <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Two-factor verification</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Enter the 6-digit code we sent to {twoFactor.sentTo || twoFactor.phone || twoFactor.email}.
        </p>
        <form onSubmit={onVerify2fa} className="space-y-4">
          <Input
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            required
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
            {loading ? "Verifying…" : "Verify"}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => setTwoFactor(null)}
          className="mt-4 text-center text-sm text-primary w-full"
        >
          Back to log in
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
      <h1 className="mb-6 font-montserrat text-2xl font-bold text-slate-900">
        {(params.get("next") || "").startsWith("/business") ? "Log in to SHUWA for Business" : "Log in to SHUWA"}
      </h1>
      <SocialAuthButtons onError={setError} onSuccess={goHome} className="mb-6" />
      <div className="relative mb-6 flex items-center">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="px-3 text-xs uppercase text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <Input
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-primary">Forgot password?</Link>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        No account? <Link to={registerTo} className="font-medium text-primary">Sign up</Link>
      </p>
    </div>
  );
}
